import './AddPeople.scss';
import { autobind } from 'office-ui-fabric-react/lib/Utilities';
import { ISharingInformation, Mode, ISharingLinkSettings } from '../../interfaces/SharingInterfaces';
import * as React from 'react';
import { EditView, IEditViewRequestType } from './EditView/EditView';
import stringsResx from '../Share.resx';
import { ActivityIndicator } from '../../components/ActivityIndicator/ActivityIndicator';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { Header } from '../Header/Header';
import { ShareViewState } from '../Share/Share';
import * as ShareCommunicationManager from '../../utilities/ShareCommunicationManager';
import * as PropTypes from 'prop-types';

export enum AddPeopleView {
  edit,
  successGrantingPermissions,
  successRequestingPermissions,
  failure,
}

export interface IAddPeopleProps {
  sharingInformation: ISharingInformation;
  clientId: number;
  currentSettings: ISharingLinkSettings;
  onViewPolicyTipClicked: () => void;
}

export interface IAddPeopleState {
  view: AddPeopleView;
  message: string;
  loading: boolean;
}

export class AddPeople extends React.Component<IAddPeopleProps, IAddPeopleState> {
  private _shareClickTime: number;

  static contextTypes = {
    shareClickTime: PropTypes.number
  };

  constructor(props: IAddPeopleProps, context: { shareClickTime: number }) {
    super(props);

    this._shareClickTime = context.shareClickTime;

    this.state = {
      message: "",
      view: AddPeopleView.edit,
      loading: false,
    };
  }

  public componentDidMount() {
    ShareCommunicationManager.pageFinishedLoading(this._shareClickTime, this.props.clientId, Mode.addPeople, '');
  }

  public render(): React.ReactElement<{}> {
    const blocker: string = this.state.loading ? " blocker" : "";
    return (
      <div className={ "od-AddPeople" + blocker }>
        { this.renderView() }
        { this.state.loading ? <ActivityIndicator message={ this.state.message } /> : null }
      </div>
    );
  }

  private renderView() {
    switch (this.state.view) {
      case AddPeopleView.edit:
        return (
          <EditView
            sharingInformation={ this.props.sharingInformation }
            clientId={ this.props.clientId }
            onLoading={ this._onLoading }
            onSuccess={ this._onSuccess }
            onFailure={ this._onFailure }
            currentSettings={ this.props.currentSettings }
            onViewPolicyTipClicked={ this.props.onViewPolicyTipClicked }
          />
        );
      case AddPeopleView.successGrantingPermissions:
        return this._renderSuccessGranting();
      case AddPeopleView.successRequestingPermissions:
        return this._renderSuccessRequesting();
      case AddPeopleView.failure:
        return this._renderFailure();
    }
  }

  @autobind
  private _onLoading(result: IEditViewRequestType) {

    this.setState((prevState, props) => {

      if (prevState.view === AddPeopleView.edit) {

        let loadingMessage: string;
        if (result.requestedAccess) {
          loadingMessage = stringsResx.activityRequestingAccess;
        } else {
          loadingMessage = result.notifiedRecipients ? stringsResx.activityAddingAndNotifying : stringsResx.activityAdding;
        }

        return {
          ...prevState,
          loading: true,
          message: loadingMessage,
        };
      } else {
        return prevState;
      }
    });
  }

  @autobind
  private _onSuccess(result: IEditViewRequestType) {

    let successMessage: string;
    let successView: AddPeopleView;
    if (result.requestedAccess) {
      successMessage = stringsResx.accessRequested;
      successView = AddPeopleView.successRequestingPermissions;
    } else {
      successMessage = result.notifiedRecipients ? stringsResx.addedAndNotified : stringsResx.added;
      successView = AddPeopleView.successGrantingPermissions;
    }

    this.setState({
      view: successView,
      message: successMessage,
      loading: false,
    });
  }

  @autobind
  private _onFailure(result: IEditViewRequestType) {
    this.setState({
      view: AddPeopleView.failure,
      message: stringsResx.addFailed,
      loading: false,
    });
  }

  private _renderSuccessGranting() {
    return (
      <div className={ 'od-AddPeople-Success' }>
        <Header
          clientId={ this.props.clientId }
          viewState={ ShareViewState.notify }     // This keeps the header itself invisible so we just get the dismiss button
        />
        <Icon className='od-ShareNotification-icon' iconName='CheckMark' />
        <p className='od-AddPeople-SuccessMessage' role='presentation'>{ this.state.message }</p>
        <input autoFocus className='od-Share-screenReaderOnly' role='alert' aria-live='assertive' aria-label={ this.state.message } value={ this.state.message } />
      </div>
    );
  }

  private _renderSuccessRequesting() {
    return (
      <div className={ 'od-AddPeople-Success' }>
        <Header
          clientId={ this.props.clientId }
          viewState={ ShareViewState.notify }     // This keeps the header itself invisible so we just get the dismiss button
        />
        <Icon className='od-ShareNotification-icon' iconName='PeopleAlert' />
        <p className='od-AddPeople-SuccessMessage' role='presentation'>{ this.state.message }</p>
        <input autoFocus className='od-Share-screenReaderOnly' role='alert' aria-live='assertive' aria-label={ this.state.message } value={ this.state.message } />
      </div>
    );
  }

  private _renderFailure() {
    return (
      <div className={ 'od-AddPeople-Failure' }>
        <Header
          clientId={ this.props.clientId }
          viewState={ ShareViewState.notify }     // This keeps the header itself invisible so we just get the dismiss button
        />
        <p>{ this.state.message }</p>
      </div>
    );
  }
}